<template>
  <div>
    <booking-steps :step="order.step" />
    
    <div class="container">
      <div class="header mb-3"></div>

      <div class="row">
        <div class="col-lg-4 col-12 order-sm-last">
          <booking-summary
              :cart="cart"
              :showtime="data.showtime"
              :step="order.step"
              :amount="order.amount"
              @show:modal="onModalShow"

          />

          <booking-concession-suggestion
              :step="order.step"
              :cart="cart"
              :concessions="data.concessions"
              @update:concession="onConcessionUpdate"
          />

          <div class="flow-actions sticky-footer-bars">
            <div class="row">
              <div class="col-3">
                <a href="#" class="btn btn-lg btn-outline-dark btn-block"
                   :class="{ 'disabled' : order.step === 1 }"
                   @click.prevent="stepHandler('prev')"
                >
                  &larr;
                </a>
              </div>
              <div class="col-9">
                <a href="#" class="btn btn-lg btn-dark btn-block"
                   :class="{ 'disabled': cart.seats.length === 0 || order.processing }"
                   @click.prevent="stepHandler('next')"
                >
                  <span class="d-md-none">{{ useFormatting().formatCurrency(order.step === 3 ? order.amount.total : order.amount.subtotal) }} | </span>
                  <span v-if="order.step !== 3">Tiếp tục</span>
                  <span v-else>{{ order.processing ? 'Đang xử lý...' : 'Xác nhận' }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-8 col-12">
          <div class="alert alert-danger" v-if="errors.length > 0">
            <p><strong>Có lỗi xảy ra:</strong></p>
            <ul>
              <li v-for="error in errors" :key="error">{{ error }}</li>
            </ul>
            <p class="mb-0">
              Vui lòng thử lại hoặc <a href="/support" class="text-white">liên hệ trung tâm hỗ trợ</a>.
            </p>
          </div>

          <div v-if="errors.length === 0">
            <booking-seats
              :step="order.step"
              :seats="data.seats" 
              @seat-click="seatPicker" 
            />

            <booking-concessions
              :step="order.step"
              :concessions="data.concessions"
              @update:concession="onConcessionUpdate"
            />

            <booking-checkout
                :cart="cart"
                :user="data.user"
                :showtime="data.showtime"
                :beta="beta"
                v-model:contact="order.contact"
                v-model:payment="order.payment"
                :amount="order.amount"
                :step="order.step"
                :completing="order.completing"
                :violations="order.violations"
                :errors="order.errors"
                @create:booking="createBooking"
                @open:modal="useBootstrap().openModal"
                @update:concession="onConcessionUpdate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, inject, onBeforeMount } from 'vue'
import axios from 'axios'
import { useFormatting } from '../composables/formatting';
import { useBootstrap } from '../composables/bootstrap';
import { bindStickyBar } from '../dependencies/sticky-bar'
import { User } from 'configcat-js';

import BookingSteps from './BookingSteps';
import BookingSummary from './BookingSummary';
import BookingSeats from './BookingSeats';
import BookingConcessions from './BookingConcessions';
import BookingConcessionSuggestion from './BookingConcessionSuggestion';
import BookingCheckout from './BookingCheckout';

const configCatClient = inject('configCatClient');

const props = defineProps({
  showtimeId: {
    type: String,
    required: true
  },
  showtime: {
    type: String,
    required: true
  },
  user: {
    type: String,
    required: true,
  }
})

const root = inject('root')

const data = ref({
  order: {
    id: null,
    seats: [],
    concessions: [],
  },
  showtime: {},
  user: {
    id: null,
    authenticated: false,
    balance: 0,
  },
  seats: {
    rows: [],
    categories: [],
    seats: [],
    groups: [],
  },
  concessions: [],
  ready: false,
})
const cart = ref({
  seats: [],
  concessions: []
})
const order = ref({
  id: null,
  step: 1,
  contact: {
    name: null,
    phone: null,
    email: null,
  },
  payment: {
    gateway: 'moveek',
    method: 'bank-transfer'
  },
  amount: {
    subtotal: 0,
    total: 0,
    discount: 0,
    fee: 0,
  },
  processing: false,
  completing: false,
  reload: false,
  violations: [],
  errors: [],
})
const errors = ref([])

const beta = ref(false)

const stepHandler = (action) => {
  if (action === 'next') {
    // if step is 3, and click `Thanh toán` button
    if (order.value.step === 3) {
      createOrder()
    }

    if (order.value.step < 3) {
      order.value.step++
    }

    // handle when concession is empty
    if (order.value.step === 2 && data.value.concessions.length === 0) {
      stepHandler('next')
    }
  } else {
    if (order.value.step > 0) {
      order.value.step--
    }

    // handle when concession is empty
    if (order.value.step === 2 && data.value.concessions.length === 0) {
      stepHandler('prev')
    }
  }
}

const getShowtime = () => {
  data.value.showtime = JSON.parse(props.showtime)
}

const getUser = () => {
  data.value.user = JSON.parse(props.user)
}

const getSeats = async () => {
  try {
    const response = await axios.get(`/booking/${props.showtimeId}/seats`)
    data.value.seats.rows = response.data.rows
    data.value.seats.categories = response.data.categories

    let seats = []
    let groups = []
    response.data.seats.forEach((rows, rowIndex) => {
      seats[rowIndex] = []
      let seatIndex = 0
      rows.forEach(seat => {
        if (seat.id) {
          // handle couple seat as group
          let groupIndex = null
          if (seat.category.couple === true && seat.size === 1) {
            groupIndex = Math.floor(seatIndex / 2)
            groupIndex = '' + rowIndex + groupIndex
          }

          seat = {
            ...seat,
            ...{ picked: false, group: groupIndex, index: seatIndex }
          }

          // handle couple seat as group
          if (seat.group !== null) {
            if (groups[groupIndex] === undefined) {
              groups[groupIndex] = []
            }

            groups[groupIndex].push(seat)
          }

          seatIndex++
        }

        seats[rowIndex].push(seat)
      })
    })

    data.value.seats.seats = seats
    data.value.seats.groups = groups
    data.value.ready = true
  } catch (error) {
    if (error?.response?.status === 400) {
      errors.value.push('Không tải được dữ liệu ghế do suất chiếu không khả dụng hoặc đã quá thời gian')
    } else {
      errors.value.push('Không tải được dữ liệu ghế do có lỗi từ hệ thống đối tác')
    }
  }
}

const getConcessions = async () => {
  try {
    const response = await axios.get(`/booking/${props.showtimeId}/concessions`)
    data.value.concessions = response.data.map(item => ({
      ...item,
      ...{ quantity: 0 }
    }))
  } catch (error) {
    // Handle error if needed
  }
}

const getOrder = async () => {
  const params = new URLSearchParams(window.location.search)
  const orderId = params.get('orderId')
  if (orderId !== null) {
    try {
      const response = await axios.get(`/booking/orders/${orderId}`)

      // update existing order to handle prefill
      data.value.order.id = response.data.id
      data.value.order.seats = response.data.seats
      data.value.order.concessions = response.data.concessions

      // also notify to creating order
      order.value.contact = response.data.contact
      order.value.reload = true
    } catch (error) {
      // Handle error if needed
    }
  }
}

const seatPicker = (seat) => {
  if (seat.taken) {
    return
  }

  if (seat.group !== null) {
    seatGroupPicker(seat.group)
  } else {
    seatSinglePicker(seat)
  }
}

const seatSinglePicker = (seat) => {
  const maxSeats = data.value.showtime.cinema.adapter === 'beta' ? 8 : 10
  const index = getPickedSeatIndex(seat)
  if (index === null) { // not picked
    // handle max seats pick
    if (cart.value.seats.length >= maxSeats) {
      useBootstrap().showModal(
        `Chỉ cho phép đặt tối đa ${maxSeats} ghế trong 1 lần đặt vé.`,
        'Đóng'
      )
      return
    }

    seat.picked = true
    cart.value.seats.push(seat)
  } else {
    seat.picked = false
    cart.value.seats.splice(index, 1)
  }
}

const seatGroupPicker = (groupIndex) => {
  data.value.seats.groups[groupIndex].forEach(seat => {
    seatSinglePicker(seat)
  })
}

const getPickedSeatIndex = (seat) => {
  let index = null
  cart.value.seats.forEach((s, i) => {
    if (s.id === seat.id) {
      index = i
    }
  })
  return index
}

const concessionPicker = (concession, action, quantity = 1) => {
  if (action === 'add') {
    concession.quantity = concession.quantity + quantity
  } else {
    concession.quantity = concession.quantity - quantity
  }

  const index = getPickedConcessionIndex(concession)
  if (index === null && concession.quantity > 0) {
    cart.value.concessions.push(concession)
  }

  if (index !== null && concession.quantity === 0) {
    cart.value.concessions.splice(index, 1)
  }
}

const getPickedConcessionIndex = (concession) => {
  let index = null
  cart.value.concessions.forEach((c, i) => {
    if (c.id === concession.id) {
      index = i
    }
  })
  return index
}

const onModalShow = ({ message, label }) => {
  useBootstrap().showModal(message, label)
}

const onConcessionUpdate = ({ concession, action }) => {
  concessionPicker(concession, action)
}

const reloadOrder = () => {
  const seats = []
  data.value.order?.seats.forEach(seat => {
    seats.push(seat.id)
  })

  data.value.seats.seats.forEach(rows => {
    rows.forEach(seat => {
      if (seats.includes(seat.id)) {
        seatPicker(seat)
      }
    })
  })

  const concessions = []
  data.value.order?.concessions.forEach(concession => {
    concessions[concession.id] = concession.quantity
  })

  data.value.concessions.forEach(concession => {
    if (concession.id in concessions) {
      concessionPicker(concession, 'add', concessions[concession.id].quantity)
    }
  })
}

const createOrder = async () => {
  if (order.value.processing === false) {
    order.value.processing = true

    const payload = {
      contact: order.value.contact,
      seats: [],
      concessions: [],
    }

    cart.value.seats.forEach(seat => {
      payload.seats.push({
        id: seat.id,
        name: seat.name,
        price: seat.price,
        category: {
          id: seat.category.id,
          name: seat.category.name,
          couple: seat.category.couple,
        }
      })
    })

    cart.value.concessions.forEach(concession => {
      if (concession.quantity > 0) {
        payload.concessions.push({
          id: concession.id,
          name: concession.name,
          price: concession.price,
          quantity: concession.quantity,
        })
      }
    })

    // clear old errors
    Object.keys(order.value.violations).forEach(k => delete order.value.violations[k])

    // create order
    try {
      const response = await axios.post(`/booking/${props.showtimeId}/orders`, payload)
      order.value.id = response.data.id

      // save customer
      saveCustomer()

      // show order confirmation modal
      useBootstrap().openModal('orderConfirmationModal')
    } catch (error) {
      const response = error?.response
      // handle validation
      if (response?.status === 400 && Array.isArray(response.data.violations)) {
        response.data.violations.forEach(v => {
          // turn "[contact][name]" into "contact.name"
          const key = v.propertyPath
              .replace(/\[(\w+)]/g, '.$1')
              .replace(/^\./, '')
          order.value.violations[key] = v.title
        })
      } else {
        console.log(error)
        alert('Không thể tạo đơn hàng do lỗi chưa xác định. Vui lòng thử lại sau.')
      }
    }
  }

  order.value.processing = false
}

const createBooking = async () => {
  if (order.value.completing === false) {
    order.value.completing = true

    // clear old errors
    order.value.errors = []

    // create booking
    try {
      const payload = {
        status: 'awaiting_payment',
        payment_gateway: order.value.payment.gateway,
        payment_method: order.value.payment.method,
      }
      const response = await axios.patch(`/booking/orders/${order.value.id}`, payload)

      window.location = response.data.redirect_url
    } catch (error) {
      const response = error?.response
      if (response?.status === 409) {
        order.value.errors.push('Ghế bạn chọn có thể đã được bán hoặc trong trạng thái giữ ghế. Vui lòng chờ trong giây lát hoặc <a href="/mua-ve/' + data.value.showtime.id + '" class="text-white-50">chọn lại</a> ghế khác.')
      } else if (response?.status === 429) {
        order.value.errors.push('Hệ thống ghi nhận bạn đang thực hiện nhiều đơn hàng đồng thời. Vui lòng chờ trong giây lát hoặc <a href="/tiep-tuc/' + data.value.showtime.id + '" class="text-white-50">hủy các đơn hàng đang chờ thanh toán</a>.')
      } else if (response?.status === 403) {
        order.value.errors.push('Đơn hàng này đã được thanh toán hoặc đã hết hạn. Vui lòng <a href="/mua-ve/' + data.value.showtime.id + '" class="text-white-50">thử lại</a> hoặc <a href="/support" class="text-white-50">liên hệ trung tâm hỗ trợ</a>.')
      } else if (response?.status === 400 && Array.isArray(response.data.violations)) {
        response.data.violations.forEach(v => {
          // turn "[contact][name]" into "contact.name"
          const key = v.propertyPath
              .replace(/\[(\w+)]/g, '.$1')
              .replace(/^\./, '')
          order.value.errors.push(key +': ' + v.title)
        })
      }
      else {
        alert('Không thể tạo đơn hàng do lỗi chưa xác định. Vui lòng thử lại sau.')
      }

      order.value.completing = false
    }
  }
}

const getCustomer = () => {
  if (order.value.reload) {
    return
  }

  try {
    const customer = root.getCookie().get('mvtix_customer')
    if (customer) {
      const parsed = JSON.parse(customer)
      order.value.contact.name = parsed?.name
      order.value.contact.phone = parsed?.phone
      order.value.contact.email = parsed?.email
    }
  } catch (e) {
  }
}

const saveCustomer = () => {
  if (order.value.reload) {
    return
  }

  const customer = {
    name: order.value.contact.name,
    email: order.value.contact.email,
    phone: order.value.contact.phone
  }
  root.getCookie().set('mvtix_customer', customer, { expires: 365 })
}

const getFeatures = () => {
  configCatClient.getValueAsync(
      'zlpApplePay',
      false,
      new User(data.value.user.email),
  ).then((value) => {
    beta.value = value;
  });
}

// watch cart changes to update order amount
watch(cart, () => {
  // calculate order sub-total
  let subtotal = 0
  cart.value.seats.forEach(seat => {
    subtotal += seat.price
  })

  let discount = 0
  cart.value.concessions.forEach(concession => {
    subtotal += concession.price * concession.quantity
    discount += concession.discount * concession.quantity
  })
  order.value.amount.subtotal = subtotal

  // calculate order fee
  const feePercent = 5
  const denominator = 2
  const number = (order.value.amount.subtotal * feePercent / 100) / 1000;
  let x = number * denominator
  x = Math.ceil(x)
  x /= denominator
  order.value.amount.fee = x * 1000

  // calculate order discount
  order.value.amount.discount = discount

  // calculate order total
  order.value.amount.total = order.value.amount.subtotal + order.value.amount.fee - order.value.amount.discount
}, { deep: true })

// watch ready state for existing order to support rebuy functionality
watch(() => data.value.ready && data.value.order.id, () => {
  if (data.value.ready === true && data.value.order.id) {
    reloadOrder()
  }
}, { deep: true })

// initialize
onMounted(() => {
  getSeats()
  getConcessions()
  getOrder()
  getCustomer()
  setTimeout(() => {
    bindStickyBar()
  }, 500)
})

onBeforeMount(() => {
  getShowtime()
  getUser()
  getFeatures()
})
</script>

<style scoped>
</style>